import type { AiSuggestionInteractionPoint } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';

import type { AiSuggestionsPlugin } from '../aiSuggestionsPluginType';
import type { RejectSuggestionExperience } from '../experiences/reject-suggestion-experience';
import { aiSuggestionsPluginKey } from '../pm-plugins/main';
import {
	addAiResultDiscardedUnifiedAnalytics,
	addDiscardSuggestionAnalytics,
} from '../ui/utils/analytics';
import { updateSuggestionStatus } from '../ui/utils/api';
import type { DiscardSuggestionAction } from './types';

export const discardSuggestionAction: (
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	rejectSuggestionExperience?: RejectSuggestionExperience,
) => DiscardSuggestionAction =
	(api, rejectSuggestionExperience) =>
	(id: string, interactionPoint: AiSuggestionInteractionPoint = 'sidebar'): boolean => {
		const experience = rejectSuggestionExperience?.start(interactionPoint);
		try {
			let found = false;
			const dispatched = api?.core?.actions.execute(({ tr }) => {
				const pluginState = api?.aiSuggestions?.sharedState.currentState();
				const suggestion = pluginState?.suggestionById.get(id);
				if (!suggestion) {
					return tr;
				}
				found = true;
				addDiscardSuggestionAnalytics(api?.analytics?.actions)(tr, suggestion, interactionPoint);
				addAiResultDiscardedUnifiedAnalytics(api?.analytics?.actions)(
					tr,
					suggestion,
					interactionPoint,
				);
				tr.setMeta(aiSuggestionsPluginKey, { action: 'REMOVE_SUGGESTION', id });
				api?.showDiff?.commands.hideDiff?.({ tr });
				return tr;
			});

			if (!dispatched || rejectSuggestionExperience?.isRejected(id) === false) {
				experience?.failure({ reason: 'rejectTransactionNotApplied' });
			} else if (!found) {
				experience?.failure({ reason: 'suggestionNotFound' });
			}

			if (found) {
				updateSuggestionStatus({ updates: [{ id, status: 'DISMISSED' }] }).then(
					() => experience?.success(),
					() => experience?.failure({ reason: 'updateStatusFailed' }),
				);
			}

			return found;
		} catch (error) {
			experience?.failure({ reason: 'rejectSuggestionError' });
			throw error;
		}
	};
